import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  KioskTicketTemplate,
  UpdateKioskTicketTemplateRequest,
  CreateKioskTicketTemplateRequest,
  DeleteKioskTicketTemplateRequest,
  GetKioskTicketTemplatesRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';
import { omit } from 'lodash';

export function* getKioskTicketTemplatesAsync({ payload }: GetKioskTicketTemplatesRequest) {
  yield put({ type: types.GET_KIOSK_TICKET_TEMPLATES.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/ticketTemplates`);
    const response: KioskTicketTemplate[] = yield call(api.getAll);

    yield put({ type: types.GET_KIOSK_TICKET_TEMPLATES.SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_KIOSK_TICKET_TEMPLATES.ERROR, payload: 'Failed to get ticketTemplates' });
  } finally {
    yield put({ type: types.GET_KIOSK_TICKET_TEMPLATES.PENDING, payload: false });
  }
}

export function* createKioskTicketTemplateAsync({ payload }: CreateKioskTicketTemplateRequest) {
  yield put({ type: types.CREATE_KIOSK_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/ticketTemplates`);
    yield call(api.create, { ...omit(payload, 'kioskId') });

    yield put({ type: types.CREATE_KIOSK_TICKET_TEMPLATE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.CREATE_KIOSK_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.CREATE_KIOSK_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export function* updateKioskTicketTemplateAsync({ payload }: UpdateKioskTicketTemplateRequest) {
  yield put({ type: types.UPDATE_KIOSK_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/ticketTemplates`);
    const response: KioskTicketTemplate = yield call(api.update, { ...omit(payload, 'kioskId') });

    yield put({ type: types.UPDATE_KIOSK_TICKET_TEMPLATE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.UPDATE_KIOSK_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.UPDATE_KIOSK_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export function* deleteKioskTicketTemplateAsync({ payload }: DeleteKioskTicketTemplateRequest) {
  yield put({ type: types.DELETE_KIOSK_TICKET_TEMPLATE.PENDING, payload: true });

  try {
    const { id, kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/ticketTemplates`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_KIOSK_TICKET_TEMPLATE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_KIOSK_TICKET_TEMPLATE.ERROR, payload: 'Server error' });
    } else {
      yield put({
        type: types.DELETE_KIOSK_TICKET_TEMPLATE.ERROR,
        payload: 'Uh oh! Something went wrong. Please try again.'
      });
    }
  } finally {
    yield put({ type: types.DELETE_KIOSK_TICKET_TEMPLATE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_KIOSK_TICKET_TEMPLATES.REQUEST, getKioskTicketTemplatesAsync),
  takeLatest(types.UPDATE_KIOSK_TICKET_TEMPLATE.REQUEST, updateKioskTicketTemplateAsync),
  takeLatest(types.CREATE_KIOSK_TICKET_TEMPLATE.REQUEST, createKioskTicketTemplateAsync),
  takeLatest(types.DELETE_KIOSK_TICKET_TEMPLATE.REQUEST, deleteKioskTicketTemplateAsync)
];
